import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../../DataGridFlexSales';

import {
  reportCollectionsFilterSelector,
  reportFilterCountryCodeSelector,
  reportFilterSelector,
  reportExtraCashReportSelector,
  downloadReportPdf,
} from '../../../state';
import { ExtraCashReport } from '../../../types';
import { getAccountingInformationReportPdfFilters } from '../getPdfFilters';
import handlerFactory, { ExtraCashReportDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<ExtraCashReport, ExtraCashReportDataGridContext>();

  const { t } = useTranslation();
  const title = t('accounting-info-report.extra-cash.title');
  const country = useSelector(reportFilterCountryCodeSelector);
  const reportExtraCashReportDataGridResource = useSelector(reportExtraCashReportSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  const { data, loading } = reportExtraCashReportDataGridResource;

  const gridContext = useMemo(
    () => ({
      t,
      country,
    }),
    [t, country]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading,
    filters: reportCollectionsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<ExtraCashReport, ExtraCashReportDataGridContext>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reportFilters = useSelector(reportFilterSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
              filters: getAccountingInformationReportPdfFilters(
                reportFilters,
                reportCollectionsFilters
              ),
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t, reportFilters, reportCollectionsFilters]);

  return null;
};

const ExtraCashDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default ExtraCashDataGrid;
